import { ComponentTypes } from "../Components/ComponentTypes";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { StringUtils, engineComponent } from "neo-common";
import { StageController } from "../Components/StageController";
import { ImageElement, ImageElementAttributes } from "../Components/Elements/Graphics/ImageElement";
import { BaseElement } from "../Components/Elements/Graphics/BaseElement";
import { Attributes } from "../Utils/Attributes";
import { PlaneRopeStyle } from "./PlaneRopeStyle";
import { StyleManager } from "../Components/Managers/StyleManager";

/**
 * !!! WARNING !!! this class does not support texture in spritesheets format !!!
 * PlaneRopeElement class is class which creates holder for image from resources with plane or rope effect
 * Example:
 *   <PlaneRopeElement Class="PlaneRopeElement" TypeAnimation="plane" AxisOfWaves="xy" NumberOfPointsX="20" NumberOfPointsY="20" Amplitude="2" Wavelength="0.15" Speed="0.004" Image="[imageId]" X="0" Y="0"/>
 *   OR
 *   <PlaneRopeElement Style="PRS_Style" />
 * @class PlaneRopeElement
 * @extends BaseElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class PlaneRopeElement extends BaseElement {
    private _planeOrRope: string;
    private _timerAnimate: FrameTimerEventEmitter;
    private _delayStopTimer: FrameTimerEventEmitter;
    private _count: number;
    private _originalVertices: Float32Array;
    private _mesh: PIXI.mesh.Mesh;
    private _meshVerticesBackup: Float32Array;
    private _points: Array<PIXI.Point> = [];
    private _originalPoints: Array<PIXI.Point> = [];
    private _graphics: PIXI.Graphics;
    private _labelText: Array<PIXI.Text> = [];
    private _debugTextColor: string;
    private _created: boolean;
    private _debug: boolean;
    private _numberOfPointsX: number;
    private _numberOfPointsY: number;
    private _amplitude: number;
    private _wavelength: number;
    private _speed: number;
    private _axisOfWaves: string;
    private _blendMode: number;
    private _resource: ImageElement;
    private _curtainSpeed: number;
    private _curtainAmplitude: number;
    private _curtainCount: number;
    private _duration: number;
    private _actualFrame: number;
    private _style: PlaneRopeStyle;
    //private _elements: ContainerElement;
    private _framesArray: Array<number>;
    private _framesPositionPointsArrayX: Object;
    private _framesPositionPointsArrayY: Object;
    private _typeFrames: string;
    private _loop: boolean;
    private _typePosition: string;
    private _canvasPadding: number = 1;
    private running: boolean = false;

    constructor() {
        super();
        this._planeOrRope = "plane";
        this._count = 0;
        this._numberOfPointsX = 20;
        this._numberOfPointsY = 20;
        this._amplitude = 3;
        this._wavelength = 0.15;
        this._speed = 0.004;
        this._axisOfWaves = "xy";
        this._created = false;
        this._blendMode = 0; // NORMAL
        this._debug = false;
        this._curtainSpeed = 0;
        this._curtainAmplitude = 0;
        this._curtainCount = 0;
        this._duration = -1; // -1 NO DURATION == LOOP
        this._actualFrame = 0;
        this._loop = false;
        this._typePosition = "relative";
        this._typeFrames = "linear";

        this._timerAnimate = CreateFrameTimer("planeTimer");
        this._timerAnimate.on(FrameTimerEvents.Complete, this.animate.bind(this));
        this._delayStopTimer = CreateFrameTimer("delayStopTimer");
        this._delayStopTimer.on(FrameTimerEvents.Complete, this.stop.bind(this));
    }

    //******************************************************************************************************************
    //  INITIALIZATION
    //******************************************************************************************************************

    public createByNode(node: Element): void {
        if (!this._created) {
            if (this.name == null) {
                this.name = node.nodeName;
            }

            if (DEBUG) {
                if (
                    node.hasAttribute(PlaneRopeElementAttributes.DEBUG) &&
                    node.getAttribute(PlaneRopeElementAttributes.DEBUG) === "true"
                ) {
                    debugger;
                    this._debug = true;

                    if (node.hasAttribute(PlaneRopeElementAttributes.DEBUG_TEXT_COLOR)) {
                        this._debugTextColor = node.getAttribute(PlaneRopeElementAttributes.DEBUG_TEXT_COLOR);
                    }
                }
            }

            super.createByNode(node);

            if (node.hasAttribute(PlaneRopeElementAttributes.NUMBER_OF_POINTS_X)) {
                this._numberOfPointsX = parseInt(node.getAttribute(PlaneRopeElementAttributes.NUMBER_OF_POINTS_X));
            }

            if (node.hasAttribute(PlaneRopeElementAttributes.NUMBER_OF_POINTS_Y)) {
                this._numberOfPointsY = parseInt(node.getAttribute(PlaneRopeElementAttributes.NUMBER_OF_POINTS_Y));
            }

            if (node.hasAttribute(PlaneRopeElementAttributes.TYPE_FRAMES)) {
                this._typeFrames = node.getAttribute(PlaneRopeElementAttributes.TYPE_FRAMES);
            }

            if (node.hasAttribute(PlaneRopeElementAttributes.TYPE_POSITION)) {
                this._typePosition = node.getAttribute(PlaneRopeElementAttributes.TYPE_POSITION);
            }

            if (node.hasAttribute(PlaneRopeElementAttributes.LOOP)) {
                this._loop = node.getAttribute(PlaneRopeElementAttributes.LOOP) == "true";
            }

            if (node.hasAttribute(PlaneRopeElementAttributes.STYLE) || this._style != null) {
                if (this._style == null) {
                    this._style = <PlaneRopeStyle>(
                        StyleManager.Styles[node.getAttribute(PlaneRopeElementAttributes.STYLE)]
                    );
                }

                if (this._style != null) {
                    // if (this._style.container != null) {
                    //     if (node.hasAttribute(PlaneRopeElementAttributes.CLONE) && StringUtils.parseBoolean(node.getAttribute(PlaneRopeElementAttributes.CLONE))) {
                    //         this._elements = this._style.container.clone();
                    //     } else {
                    //         this._elements = this._style.container;
                    //     }
                    //
                    //     this.addChild(this._elements);

                    // All number of frames
                    if (this._style.frames != null) {
                        this._framesArray = [];

                        for (let key in this._style.frames) {
                            this._framesArray.push(parseInt(key));
                        }

                        // MAX frame
                        this._duration = this._framesArray[this._framesArray.length - 1];
                    } else {
                        if (DEBUG) {
                            this.log("WARNING no planerope");
                        }
                    }

                    // position of all points X
                    if (this._style.positionX != null) {
                        this._framesPositionPointsArrayX = this._style.positionX;

                        for (let x in this._framesPositionPointsArrayX) {
                            let xArray: Array<number> = this._framesPositionPointsArrayX[x].split(";").map(parseFloat);

                            if (xArray.length != this._numberOfPointsX * this._numberOfPointsY) {
                                if (DEBUG) {
                                    this.log(
                                        "WARNING FRAME: " +
                                            x.toString() +
                                            " the number of points X is not equal to the total number of points (X*Y)",
                                    );
                                }
                            }
                        }
                    }

                    // position of all points Y
                    if (this._style.positionY != null) {
                        this._framesPositionPointsArrayY = this._style.positionY;

                        for (let y in this._framesPositionPointsArrayY) {
                            let yArray: Array<number> = this._framesPositionPointsArrayY[y].split(";").map(parseFloat);

                            if (yArray.length != this._numberOfPointsX * this._numberOfPointsY) {
                                if (DEBUG) {
                                    this.log(
                                        "WARNING FRAME: " +
                                            y.toString() +
                                            " the number of points Y is not equal to the total number of points (X*Y)",
                                    );
                                }
                            }
                        }
                    }

                    // type of animation frames
                    if (this._typeFrames === "linear") {
                        if (this._framesArray.length > 1 && this._framesArray.length != this._duration) {
                            let previousDefinedFrameIndex: number = 0;
                            let nextDefinedFrameIndex: number = 1;

                            for (let i: number = 0; i < this._duration; i++) {
                                if (
                                    i > this._framesArray[previousDefinedFrameIndex] &&
                                    i < this._framesArray[nextDefinedFrameIndex] &&
                                    (i != this._framesArray[i] || this._framesArray[i] == null) &&
                                    nextDefinedFrameIndex <= this._framesArray.length - 1
                                ) {
                                    let prevFrame: number = this._framesArray[previousDefinedFrameIndex];
                                    let nextFrame: number = this._framesArray[nextDefinedFrameIndex];
                                    let diffFrames: number = nextFrame - prevFrame; //- 1;

                                    let ArrayPrevValuesX: Array<number> = this._framesPositionPointsArrayX[prevFrame]
                                        .split(";")
                                        .map(parseFloat);
                                    let ArrayNextValuesX: Array<number> = this._framesPositionPointsArrayX[nextFrame]
                                        .split(";")
                                        .map(parseFloat);

                                    let ArrayPrevValuesY: Array<number> = this._framesPositionPointsArrayY[prevFrame]
                                        .split(";")
                                        .map(parseFloat);
                                    let ArrayNextValuesY: Array<number> = this._framesPositionPointsArrayY[nextFrame]
                                        .split(";")
                                        .map(parseFloat);

                                    for (let newFrame: number = i; newFrame < nextFrame; newFrame++) {
                                        let newFrameValuesStringX: string = "";
                                        let newFrameValuesStringY: string = "";

                                        if (this._framesPositionPointsArrayX[newFrame] == null) {
                                            for (let x: number = 0; x < ArrayPrevValuesX.length; x++) {
                                                let diffValue: number =
                                                    (ArrayNextValuesX[x] - ArrayPrevValuesX[x]) / diffFrames;
                                                newFrameValuesStringX += Math.round(
                                                    ArrayPrevValuesX[x] + diffValue * (newFrame + 1 - i),
                                                );

                                                if (x < ArrayPrevValuesX.length - 1) {
                                                    newFrameValuesStringX += ";";
                                                }
                                            }

                                            // ADD new frame
                                            this._framesPositionPointsArrayX[newFrame] = newFrameValuesStringX;
                                        }

                                        if (this._framesPositionPointsArrayY[newFrame] == null) {
                                            for (let y: number = 0; y < ArrayPrevValuesY.length; y++) {
                                                let diffValue: number =
                                                    (ArrayNextValuesY[y] - ArrayPrevValuesY[y]) / diffFrames;
                                                newFrameValuesStringY += Math.round(
                                                    ArrayPrevValuesY[y] + diffValue * (newFrame + 1 - i),
                                                );

                                                if (y < ArrayPrevValuesY.length - 1) {
                                                    newFrameValuesStringY += ";";
                                                }
                                            }

                                            // ADD new frame
                                            this._framesPositionPointsArrayY[newFrame] = newFrameValuesStringY;
                                        }
                                    }

                                    previousDefinedFrameIndex = nextDefinedFrameIndex;
                                    nextDefinedFrameIndex++;
                                    i = nextFrame;
                                }
                            }
                        }
                    }
                    // } else {
                    //     if (DEBUG) {
                    //         this.log('WARNING no children');
                    //     }
                    // }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no style with name: " + node.getAttribute(PlaneRopeElementAttributes.STYLE));
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("WARNING no style (PlaneRopeStyle) for PlaneRopeElement");
                }
            }

            if (node.hasAttribute(ImageElementAttributes.BLEND_MODE)) {
                this._blendMode = this.getBlendModeByString(
                    Attributes.assignStringAttribute(node, ImageElementAttributes.BLEND_MODE, this._blendMode),
                );
            }

            if (node.hasAttribute(ImageElementAttributes.IMAGE) && this._resource == null) {
                this._resource = new ImageElement(
                    StageController.getTextureByResourceId(node.getAttribute(ImageElementAttributes.IMAGE)),
                );
                this._resource.blendMode = this._blendMode;
            }

            if (node.hasAttribute(PlaneRopeElementAttributes.AMPLITUDE)) {
                this._amplitude = parseInt(node.getAttribute(PlaneRopeElementAttributes.AMPLITUDE));
            }

            if (node.hasAttribute(PlaneRopeElementAttributes.WAVELENGTH)) {
                this._wavelength = parseFloat(node.getAttribute(PlaneRopeElementAttributes.WAVELENGTH));
            }

            if (node.hasAttribute(PlaneRopeElementAttributes.SPEED)) {
                this._speed = parseFloat(node.getAttribute(PlaneRopeElementAttributes.SPEED));
            }

            if (node.hasAttribute(PlaneRopeElementAttributes.TYPE_ANIMATION)) {
                this._planeOrRope = node.getAttribute(PlaneRopeElementAttributes.TYPE_ANIMATION);
            }

            if (node.hasAttribute(PlaneRopeElementAttributes.AXIS_OF_WAVES)) {
                this._axisOfWaves = node.getAttribute(PlaneRopeElementAttributes.AXIS_OF_WAVES);
            }

            if (node.hasAttribute(PlaneRopeElementAttributes.CURTAIN_SPEED)) {
                this._curtainSpeed = parseInt(node.getAttribute(PlaneRopeElementAttributes.CURTAIN_SPEED));
            }

            if (node.hasAttribute(PlaneRopeElementAttributes.CURTAIN_AMPLITUDE)) {
                this._curtainAmplitude = parseInt(node.getAttribute(PlaneRopeElementAttributes.CURTAIN_AMPLITUDE));
            }

            if (node.hasAttribute(PlaneRopeElementAttributes.CANVAS_PADDING)) {
                this._canvasPadding = parseInt(node.getAttribute(PlaneRopeElementAttributes.CANVAS_PADDING));
            }

            this.createMesh();
            this.animate(); // WARNING !!! this is necessary for the first deformation of the original texture, the original undeformed texture is never displayed
            if (this._mesh) {
                // BACKUP vertices after deformed texture
                this._meshVerticesBackup = this._mesh.vertices.slice(0); // deformation mesh
            }
            this._created = true;
        }
    }

    public updateByNode(node: Element): void {
        if (DEBUG) {
            if (
                node.hasAttribute(PlaneRopeElementAttributes.DEBUG) &&
                node.getAttribute(PlaneRopeElementAttributes.DEBUG) === "true"
            ) {
                debugger;
                this._debug = true;
            }
        }

        super.updateByNode(node);

        if (node.hasAttribute(PlaneRopeElementAttributes.LOOP)) {
            this._loop = node.getAttribute(PlaneRopeElementAttributes.LOOP) === "true";
        }

        if (node.hasAttribute(PlaneRopeElementAttributes.START)) {
            let delay = 0;
            if (node.getAttribute(PlaneRopeElementAttributes.START).split(";").length > 1) {
                let from = parseInt(node.getAttribute(PlaneRopeElementAttributes.START).split(";")[0]);
                let to = parseInt(node.getAttribute(PlaneRopeElementAttributes.START).split(";")[1]);
                delay = from + Math.round(Math.random() * (to - from));
            } else {
                delay = parseInt(node.getAttribute(PlaneRopeElementAttributes.START));
                if (isNaN(delay)) {
                    delay = 0;
                }
            }
            this.start(delay);
            this._count = 0; // reset start angle position to 0 etc. "sin(this._count)"
            this._curtainCount = 0;
        }

        if (node.hasAttribute(PlaneRopeElementAttributes.STOP)) {
            //this.stop();
            this._delayStopTimer.start(parseInt(node.getAttribute(PlaneRopeElementAttributes.STOP)));
        }

        if (node.hasAttribute(PlaneRopeElementAttributes.CURTAIN_SPEED)) {
            this._curtainSpeed = parseInt(node.getAttribute(PlaneRopeElementAttributes.CURTAIN_SPEED));
        }

        if (node.hasAttribute(PlaneRopeElementAttributes.CURTAIN_AMPLITUDE)) {
            this._curtainAmplitude = parseInt(node.getAttribute(PlaneRopeElementAttributes.CURTAIN_AMPLITUDE));
        }
    }

    //******************************************************************************************************************
    //  MESH
    //******************************************************************************************************************

    private createMesh(): void {
        if (this._planeOrRope === "rope") {
            // ROPE
            let ropeLength = this._resource.texture.width;

            for (let i = 0; i < this._numberOfPointsX; i++) {
                this._points.push(new PIXI.Point(i * (ropeLength / (this._numberOfPointsX - 1)), 0));
                this._originalPoints.push(new PIXI.Point(i * (ropeLength / (this._numberOfPointsX - 1)), 0));
            }

            if (this._points && this._points.length > 0) {
                this._mesh = new PIXI.mesh.Rope(this._resource.texture, this._points);
                if (this._mesh) {
                    this._mesh.width = this._resource.texture.width;
                    this._mesh.height = this._resource.texture.height;
                    this._mesh.blendMode = this._blendMode;
                    this._mesh.canvasPadding = this._canvasPadding;
                    this.addChild(this._mesh);
                }
            }
        } else if (
            this._planeOrRope === "plane" ||
            this._planeOrRope === "regularGrid" ||
            this._planeOrRope === "curtain"
        ) {
            // PLANE and REGULAR GRID
            this._mesh = new PIXI.mesh.Plane(this._resource.texture, this._numberOfPointsX, this._numberOfPointsY);
            this._mesh.drawMode = PIXI.DRAW_MODES.LINE_LOOP;

            if (this._mesh) {
                this._mesh.width = this._resource.texture.width;
                this._mesh.height = this._resource.texture.height;
                this._mesh.blendMode = this._blendMode;
                this._mesh.canvasPadding = this._canvasPadding;
                this.addChild(this._mesh);

                this._originalVertices = this._mesh.vertices.slice(0);
            }
        } else {
        }

        if (this._debug === true) {
            this._graphics = new PIXI.Graphics();
            if (this._graphics) {
                this.addChild(this._graphics);
            }

            if (this._debugTextColor != null && this._debugTextColor != "undefined") {
                this._labelText = [];
                if (this._labelText) {
                    for (let i: number = 0; i < this._numberOfPointsX * this._numberOfPointsY; i++) {
                        let text = new PIXI.Text(i + 1 + "");
                        text.style.fill = this._debugTextColor;
                        this._labelText.push(text);
                        this.addChild(this._labelText[i]);
                    }
                }
            }
        }

        //this._timer.start(20);
    }

    private animate(): void {
        let stopTimer: boolean = false;

        this._count += this._speed;

        if (this._count >= 2 * Math.PI) {
            this._count = this._count % (2 * Math.PI);
        }

        if (this._planeOrRope === "rope") {
            // ROPE
            if (this._mesh && this._created === true && this._points && this._points.length > 0) {
                let xArray: Array<number>;
                let yArray: Array<number>;

                if (
                    this._actualFrame >= 0 &&
                    this._framesPositionPointsArrayX[this._actualFrame] != null &&
                    this._framesPositionPointsArrayX[this._actualFrame] != "undefined" &&
                    this._framesPositionPointsArrayY[this._actualFrame] != null &&
                    this._framesPositionPointsArrayY[this._actualFrame] != "undefined"
                ) {
                    xArray = this._framesPositionPointsArrayX[this._actualFrame].split(";").map(parseFloat);
                    yArray = this._framesPositionPointsArrayY[this._actualFrame].split(";").map(parseFloat);
                }

                if (
                    xArray != null &&
                    xArray.length > 0 &&
                    xArray.length == this._numberOfPointsX * this._numberOfPointsY &&
                    yArray != null &&
                    yArray.length > 0 &&
                    yArray.length == this._numberOfPointsX * this._numberOfPointsY
                ) {
                    for (let i = 0; i < this._points.length; i++) {
                        if (
                            this._typePosition === "relative" ||
                            this._typePosition == null ||
                            this._typePosition == "undefined"
                        ) {
                            // X points
                            this._points[i].x = this._originalPoints[i].x + xArray[i];
                            // Y points
                            this._points[i].y = this._originalPoints[i].y + yArray[i];
                        } else if (this._typePosition === "absolute") {
                            // X points
                            this._points[i].x = xArray[i];
                            // Y points
                            this._points[i].y = yArray[i];
                        }
                    }
                }
            }

            if (this._debug === true) {
                this.renderPointsRope();
            }
        } else if (this._planeOrRope === "plane") {
            // PLANE
            if (this._mesh && this._mesh.vertices) {
                for (let i = 0; i < this._mesh.vertices.length; i++) {
                    if (
                        (this._axisOfWaves === "x" && i % 2 == 1) ||
                        ((this._axisOfWaves === "y" || this._axisOfWaves === "yx") && i % 2 == 0) ||
                        this._axisOfWaves === "xy"
                    ) {
                        this._mesh.vertices[i] =
                            this._originalVertices[i] + this._amplitude * Math.sin(this._count + i * this._wavelength);
                    } else if (this._axisOfWaves === "yx" && i % 2 == 1) {
                        this._mesh.vertices[i] =
                            this._originalVertices[i] - this._amplitude * Math.sin(this._count + i * this._wavelength);
                    } else {
                    }
                }
            }

            if (this._debug === true) {
                this.renderPointsPlane();
            }
        } else if (this._planeOrRope === "regularGrid") {
            // REGULAR GRID
            if (
                this._mesh &&
                this._mesh.vertices &&
                this._created === true &&
                this._framesPositionPointsArrayX != null &&
                this._framesPositionPointsArrayY != null
            ) {
                let xArray: Array<number>;
                let yArray: Array<number>;

                if (
                    this._actualFrame >= 0 &&
                    this._framesPositionPointsArrayX[this._actualFrame] != null &&
                    this._framesPositionPointsArrayX[this._actualFrame] != "undefined" &&
                    this._framesPositionPointsArrayY[this._actualFrame] != null &&
                    this._framesPositionPointsArrayY[this._actualFrame] != "undefined"
                ) {
                    xArray = this._framesPositionPointsArrayX[this._actualFrame].split(";").map(parseFloat);
                    yArray = this._framesPositionPointsArrayY[this._actualFrame].split(";").map(parseFloat);
                }

                if (
                    xArray != null &&
                    xArray.length > 0 &&
                    xArray.length == this._numberOfPointsX * this._numberOfPointsY &&
                    yArray != null &&
                    yArray.length > 0 &&
                    yArray.length == this._numberOfPointsX * this._numberOfPointsY
                ) {
                    for (let i = 0; i < this._mesh.vertices.length; i++) {
                        if (
                            this._typePosition === "relative" ||
                            this._typePosition == null ||
                            this._typePosition == "undefined"
                        ) {
                            if (i % 2 == 0) {
                                // X points
                                this._mesh.vertices[i] = this._originalVertices[i] + xArray[i / 2];
                            } else {
                                // Y points
                                this._mesh.vertices[i] = this._originalVertices[i] + yArray[(i + 1) / 2 - 1];
                            }
                        } else if (this._axisOfWaves === "yx" && i % 2 == 1) {
                            if (i % 2 == 0) {
                                // X points
                                this._mesh.vertices[i] = xArray[i / 2];
                            } else {
                                // Y points
                                this._mesh.vertices[i] = yArray[(i + 1) / 2 - 1];
                            }
                        }
                    }
                }
            }

            if (this._debug === true) {
                this.renderPointsPlane();
            }
        } else if (this._planeOrRope === "curtain") {
            // CURTAIN
            if (
                this._curtainSpeed > 0 &&
                this._numberOfPointsX > 0 &&
                this._numberOfPointsY > 0 &&
                this._mesh.vertices.length == this._meshVerticesBackup.length
            ) {
                for (let i = 0; i < this._mesh.vertices.length; i++) {
                    let row_index: number = parseInt((i / (this._numberOfPointsX * 2)).toString()).valueOf();
                    let col_index: number = parseInt(((i / 2) % this._numberOfPointsX).toString()).valueOf();

                    if (i % 2 == 0) {
                        // % 2 == 0 is X point
                        if (row_index % 2 == 0) {
                            this._mesh.vertices[i] =
                                this._meshVerticesBackup[i] -
                                this._curtainAmplitude *
                                    (row_index + 1) *
                                    (this._numberOfPointsX - (col_index + 1)) *
                                    Math.sin(this._curtainCount);
                        } else {
                            this._mesh.vertices[i] =
                                this._meshVerticesBackup[i] +
                                this._curtainAmplitude *
                                    (row_index + 1) *
                                    (this._numberOfPointsX - (col_index + 1)) *
                                    Math.sin(this._curtainCount);
                        }
                    }
                }

                this._curtainCount += Math.PI / this._curtainSpeed;

                // curtain has only angle from 0 to 180 degrees (0 rad to PI rad)
                if (this._curtainCount > Math.PI) {
                    // end of animation, show original vertices, then this timer stop
                    this._curtainSpeed = 0; // off curtain
                    this._curtainCount = 0;
                    stopTimer = true;
                }
            }

            if (this._debug === true) {
                this.renderPointsPlane();
            }
        } else {
            // bad type of planerope
        }

        //-----------------------------------

        if (this._created === true) {
            if (this._duration >= 0) {
                this._actualFrame++;
            }

            if ((this._duration >= 0 && this._actualFrame > this._duration) || stopTimer == true) {
                this.stop();
            } else {
                // first step not start timer (creating mesh) and can stop timer by boolean
                this._timerAnimate.start(0);
            }
        }
    }

    //******************************************************************************************************************
    //  FOR DEBUG, RENDER POINTS
    //******************************************************************************************************************

    private renderPointsRope(): void {
        if (this._graphics) {
            if (this._points != null && this._points.length > 0) {
                this._graphics.clear();

                this._graphics.lineStyle(2, 0xffc2c2);
                this._graphics.moveTo(this._points[0].x, this._points[0].y);

                for (let i = 1; i < this._points.length; i++) {
                    this._graphics.lineTo(this._points[i].x, this._points[i].y);
                }

                for (let i = 0; i < this._points.length; i++) {
                    this._graphics.beginFill(0xff0022);
                    this._graphics.drawCircle(this._points[i].x, this._points[i].y, 10);
                    this._graphics.endFill();

                    if (
                        this._debugTextColor != null &&
                        this._debugTextColor != "undefined" &&
                        this._labelText != null
                    ) {
                        if (this._labelText[i] != null) {
                            this._labelText[i].x = this._points[i].x;
                            this._labelText[i].y = this._points[i].y;
                        }
                    }
                }
            }
        }
    }

    private renderPointsPlane(): void {
        if (this._graphics) {
            if (this._mesh && this._mesh.vertices.length > 0) {
                this._graphics.clear();

                this._graphics.moveTo(this._mesh.vertices[0], this._mesh.vertices[1]);

                for (let i = 0; i < this._mesh.vertices.length; i++) {
                    this._graphics.beginFill(0xff0022);
                    this._graphics.drawCircle(this._mesh.vertices[i], this._mesh.vertices[++i], 5);
                    this._graphics.endFill();

                    if (
                        this._debugTextColor != null &&
                        this._debugTextColor != "undefined" &&
                        this._labelText != null
                    ) {
                        if (this._labelText[(i - 1) / 2] != null) {
                            this._labelText[(i - 1) / 2].x = this._mesh.vertices[i - 1];
                            this._labelText[(i - 1) / 2].y = this._mesh.vertices[i];
                        }
                    }
                }
            }
        }
    }

    //******************************************************************************************************************
    //  CONTROLS
    //******************************************************************************************************************

    public start(startDelay: number = 0): void {
        if (this.running) {
            return;
        }

        if (startDelay < 0) {
            startDelay = 20;
        }

        this._timerAnimate.stop();
        this._timerAnimate.start(startDelay);
        this.running = true;
    }

    public stop(): void {
        this._timerAnimate.stop();
        this.running = false;
        this._actualFrame = 0;

        if (this._loop) {
            this.start();
        }
    }

    //******************************************************************************************************************
    //  BLEND
    //******************************************************************************************************************

    /**
     * Important! The WebGL renderer only supports the NORMAL, ADD, MULTIPLY and SCREEN blend modes.
     * Anything else will silently act like NORMAL.
     *
     * @param mode
     * @returns {number}
     */
    public getBlendModeByString(mode: string): number {
        mode = mode.toString();
        switch (mode) {
            case "0":
            case "normal":
                return PIXI.BLEND_MODES.NORMAL;
            case "1":
            case "add":
                return PIXI.BLEND_MODES.ADD;
            case "2":
            case "multiply":
                return PIXI.BLEND_MODES.MULTIPLY;
            case "3":
            case "screen":
                return PIXI.BLEND_MODES.SCREEN;
            case "4":
            case "overlay":
                return PIXI.BLEND_MODES.OVERLAY;
            case "5":
            case "darken":
                return PIXI.BLEND_MODES.DARKEN;
            case "6":
            case "lighten":
                return PIXI.BLEND_MODES.LIGHTEN;
            case "7":
            case "color_dodge":
                return PIXI.BLEND_MODES.COLOR_DODGE;
            case "8":
            case "color_burn":
                return PIXI.BLEND_MODES.COLOR_BURN;
            case "9":
            case "hard_light":
                return PIXI.BLEND_MODES.HARD_LIGHT;
            case "10":
            case "soft_light":
                return PIXI.BLEND_MODES.SOFT_LIGHT;
            case "11":
            case "difference":
                return PIXI.BLEND_MODES.DIFFERENCE;
            case "12":
            case "exclusion":
                return PIXI.BLEND_MODES.EXCLUSION;
            case "13":
            case "hue":
                return PIXI.BLEND_MODES.HUE;
            case "14":
            case "saturation":
                return PIXI.BLEND_MODES.SATURATION;
            case "15":
            case "color":
                return PIXI.BLEND_MODES.COLOR;
            case "16":
            case "luminosity":
                return PIXI.BLEND_MODES.LUMINOSITY;
            default:
                if (DEBUG) {
                    this.log("ERROR Unknown blending mode " + mode + ". Used default mode NORMAL.");
                }
                return PIXI.BLEND_MODES.NORMAL;
        }
    }

    //******************************************************************************************************************
    //  CLONE
    //******************************************************************************************************************

    /**
     * Clone method used for cloning instance of this element with his main properties.
     * @method clone
     * @return PlaneRopeElement
     */
    clone(): PlaneRopeElement {
        let element: PlaneRopeElement = new PlaneRopeElement();
        element._numberOfPointsX = this._numberOfPointsX;
        element._numberOfPointsY = this._numberOfPointsY;
        element._amplitude = this._amplitude;
        element._wavelength = this._wavelength;
        element._speed = this._speed;
        element._axisOfWaves = this._axisOfWaves;
        element._resource = this._resource.clone();
        element._blendMode = this._blendMode;
        element._planeOrRope = this._planeOrRope;
        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.visible = this.visible;
        element._debug = this._debug;
        element._debugTextColor = this._debugTextColor;
        element._canvasPadding = this._canvasPadding;
        element._style = this._style;
        element._loop = this._loop;
        // element._created = this._created;
        // element._count = this._count;
        // element._duration = this._duration;
        // element._framesPositionPointsArrayX = this._framesPositionPointsArrayX;
        // element._framesPositionPointsArrayY = this._framesPositionPointsArrayY;
        // element._mesh = this._mesh;

        let node: Element = document.createElement(this.name);
        element.create(node);
        element._markAsCloned(); // must be called last before return!
        return element;
    }
}

export const PlaneRopeElementAttributes = {
    /** Canvas padding for mesh (overlay of mesh polygons to avoid gaps)
     * @property CanvasPadding
     * @type number
     * @default 1
     */
    CANVAS_PADDING: "CanvasPadding",
    /**
     * debug attribute must be parent only
     * */
    DEBUG: "Debug",
    /** Text color for DEBUG, showing number of point
     * @property DebugTextColor
     * @type string
     * @default "#FFFA00"
     */
    DEBUG_TEXT_COLOR: "DebugTextColor",
    /**
     * plane, rope and other
     * @property TypeAnimation
     * @type string
     * @default plane
     * VALUES:
     *      rope
     *      plane
     *      curtain
     *      regularGrid
     */
    TYPE_ANIMATION: "TypeAnimation",
    /**
     * number of points in axis X,
     * for 'plane, curtain, regularGrid' is number of columns
     * for 'rope' is number of points in line axis X
     * @property NumberOfPointsX
     * @type number
     * @default 0
     * */
    NUMBER_OF_POINTS_X: "NumberOfPointsX",
    /**
     * number of points in axis Y,
     * for 'plane, curtain, regularGrid' is number of rows
     * for 'rope' is number of points Y, must be value 1
     * @property NumberOfPointsY
     * @type number
     * @default 0
     * */
    NUMBER_OF_POINTS_Y: "NumberOfPointsY",
    /**
     * size of Amplitude moving points
     * for type 'plane'
     * @property Amplitude
     * @type number
     * @default 0
     * */
    AMPLITUDE: "Amplitude",
    /**
     * size of the angle
     * for type 'plane'
     * @property Wavelength
     * @type number
     * @default 0
     * */
    WAVELENGTH: "Wavelength",
    /**
     * speed of trigonometric animation
     * for type 'plane'
     * @property Speed
     * @type number
     * @default 0
     * */
    SPEED: "Speed",

    /**
     * function axis, x, y, xy, yx
     * for type 'plane'
     * @property AxisOfWaves
     * @type string
     * @default xy
     */
    AXIS_OF_WAVES: "AxisOfWaves",

    /**
     * start animation with delay
     * @property Start
     * @type number
     * @default 0
     */
    START: "Start",
    /**
     * stop animation with delay
     * @property Stop
     * @type number
     * @default 0
     */
    STOP: "Stop",
    /**
     * loop animation with delay
     * @property Loop
     * @type number
     * @default 0
     * */
    LOOP: "Loop",

    /**
     * speed of curtain
     * for type 'curtain'
     * @property CurtainSpeed
     * @type number
     * @default 0
     * */
    CURTAIN_SPEED: "CurtainSpeed",
    /**
     * amplitude of curtain
     * for type 'curtain'
     * @property CurtainAmplitude
     * @type number
     * @default 0
     * */
    CURTAIN_AMPLITUDE: "CurtainAmplitude",

    /**
     * clone
     * @property Clone
     * @type bool
     * @default false
     * */
    CLONE: "Clone",
    /**
     * style of 'PlaneRopeStyle'
     * @property Style
     * @type string
     * @default ''
     * */
    STYLE: "Style",

    /**
     * the type of frame calculation between frames
     * @property TypeFrames
     * @type string
     * @default 'linear'
     * VALUES:
     *      linear - automatic calculation position for frames, for example if defined frame 1 and 100, then frames 2-99 position will be calculated
     * */
    TYPE_FRAMES: "TypeFrames",

    /**
     * the type of use of positions
     * for type 'rope, regularGrid'
     * @property TypePosition
     * @type string
     * @default 'relative'
     * VALUES:
     *      relative - calculation position from original to relative shift
     *      absolute - calculation position to absolute screen position
     * */
    TYPE_POSITION: "TypePosition",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/PlaneRopeElement.ts
 **/